import { useLazyQuery } from '@apollo/client';
import { SearchInput, SearchUiTextResult, UiText } from '@bechtel/sr-types';
import { find, map, replace } from 'lodash';
import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { SEARCH_UI_TEXTS } from '~/gql/ui-text';

export interface LanguageProps {
  isAlternateLanguageUsed: boolean;
}

export interface UseLanguageResult {
  isAlternateLanguageUsed: boolean;
  getMultiLingualTextByKey: (key: string, texts?: string[]) => string;
}

// Get language text by key and alternate language used flag
function getLanguangeTextByKey(
  key: string,
  uiTexts: UiText[],
  isAlternateLanguageUsed: boolean,
  texts?: string[]
): string {
  if (!key || !uiTexts || uiTexts.length === 0) return '';
  const result = find(uiTexts, (item: UiText) => item.textKey === key || item.description === key);
  if (!result) return '';
  if (texts && texts.length > 0) {
    let value = !isAlternateLanguageUsed ? result.description : result.alternateDescription;
    map(texts, (text, idx) => {
      value = replace(value, `{${idx}}`, text);
    });
    return value;
  }
  return !isAlternateLanguageUsed ? result.description : result.alternateDescription;
}

const LanguageStateContext: React.Context<boolean> = React.createContext<boolean>(false);
const LanguageUpdaterContext: React.Context<React.Dispatch<React.SetStateAction<boolean>>> =
  //react create context accepts null, bug with type definitions
  //@ts-ignore react create context accepts null, bug with type definitions
  React.createContext<React.Dispatch<React.SetStateAction<boolean>>>(false);

function useLanguage(): UseLanguageResult {
  const isAlternateLanguageUsed = useContext(LanguageStateContext);
  if (typeof isAlternateLanguageUsed === undefined) {
    throw new Error(`useLanguage must be used within a LanguageProvider`);
  }

  const [searchUiTexts, { data }] = useLazyQuery<
    {
      searchUiTexts: SearchUiTextResult;
    },
    SearchInput
  >(SEARCH_UI_TEXTS, {
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    searchUiTexts({
      variables: {
        filterInput: { and: null },
        paginationInput: {
          limit: 500,
        },
      },
    });
  }, [searchUiTexts]);

  const getMultiLingualTextByKey = React.useCallback(
    (key: string, texts?: string[]) =>
      getLanguangeTextByKey(
        key,
        data?.searchUiTexts?.items ? data.searchUiTexts.items : [],
        isAlternateLanguageUsed,
        texts
      ),
    [data?.searchUiTexts?.items, isAlternateLanguageUsed]
  );

  return { isAlternateLanguageUsed, getMultiLingualTextByKey };
}

function useLanguageUpdater() {
  const setAlternateLanguageUsed = useContext(LanguageUpdaterContext);
  if (typeof setAlternateLanguageUsed === undefined) {
    throw new Error(`useLanguage must be used within a LanguageProvider`);
  }
  const alternateLanguageUsed = React.useCallback(
    (isAlternateLanguageUsed: boolean) => setAlternateLanguageUsed(isAlternateLanguageUsed),
    [setAlternateLanguageUsed]
  );
  return { alternateLanguageUsed };
}

function LanguageProvider(props: PropsWithChildren<LanguageProps>) {
  const [isAlternateLanguageUsed, setAlternateLanguageUsed] = useState<boolean>(false);

  return (
    <LanguageStateContext.Provider value={isAlternateLanguageUsed}>
      <LanguageUpdaterContext.Provider value={setAlternateLanguageUsed}>
        {props.children}
      </LanguageUpdaterContext.Provider>
    </LanguageStateContext.Provider>
  );
}

export { LanguageProvider, useLanguage, useLanguageUpdater };
